import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import {
	GenerateAiPetitionImageBackground,
	type GenerateAiPetitionImageBackgroundMutation,
	type GenerateAiPetitionImageBackgroundMutationVariables,
} from './api.graphql';
import { GenerateAiPetitionImageBackgroundError } from './errors';

export type AiImageInput = { mainPrompt?: string; personalStory?: string };

type GenerateAiImageReturn = {
	responseId: string;
};

export async function generateAiPetitionImage(
	input: AiImageInput,
	utilityContext: UtilityContext,
): Promise<GenerateAiImageReturn> {
	const {
		gql: { fetch },
		errorReporter,
		tracker,
	} = utilityContext;

	// eslint-disable-next-line @typescript-eslint/no-deprecated
	void tracker.track('sap_ai_image_generate');

	try {
		if (!input.mainPrompt)
			throw new GenerateAiPetitionImageBackgroundError(undefined, { cause: new Error('Main prompt is required') });

		const { data } = await fetch<
			GenerateAiPetitionImageBackgroundMutation,
			GenerateAiPetitionImageBackgroundMutationVariables
		>({
			query: GenerateAiPetitionImageBackground,
			path: '/generate-ai-petition-image',
			variables: {
				input: {
					title: input.mainPrompt,
					description: input.personalStory || '',
					backgroundMode: true,
				},
			},
			rejectOnError: true,
		});

		if (data.generateAiPetitionImage.__typename !== 'GenerateAiPetitionImageBackgroundSuccess')
			throw new GenerateAiPetitionImageBackgroundError(data.generateAiPetitionImage.__typename);

		return {
			responseId: data.generateAiPetitionImage.responseId,
		};
	} catch (e: unknown) {
		const error =
			e instanceof GenerateAiPetitionImageBackgroundError
				? e
				: new GenerateAiPetitionImageBackgroundError(undefined, { cause: e });

		// eslint-disable-next-line @typescript-eslint/no-deprecated
		void tracker.track('sap_ai_image_generate_error');

		void errorReporter.report({ error });
		throw error;
	}
}
